interface IGoogleAddressResult {
  address_components?: { types: string[]; long_name: string }[];
}

/**
 * Extracts the sublocality, locality, or administrative area from Google address components.
 * Prefers locality, then sublocality, and finally administrative area as the town name.
 */
export const getTownFromGoogleAddress = (
  googleAddressResult: IGoogleAddressResult,
): string => {
  const addressComponents = googleAddressResult.address_components || [];
  let sublocalityName = "";
  let localityName = "";
  let administrativeAreaName = "";
  for (const component of addressComponents) {
    const { types, long_name: name } = component;
    if (types.includes("sublocality")) {
      sublocalityName = name;
    }
    if (types.includes("locality")) {
      localityName = name;
    }
    if (types.includes("administrative_area")) {
      administrativeAreaName = name;
    }
  }
  // Prioritize locality, then sublocality, then administrative area
  return localityName || sublocalityName || administrativeAreaName;
};

/**
 * Extracts the emirate or primary administrative area from Google address components.
 * Prefers administrative area level 1, then locality.
 */
export const getEmirateFromGoogleAddress = (
  googleAddressResult: IGoogleAddressResult,
): string => {
  const addressComponents = googleAddressResult.address_components || [];
  let primaryAdminArea = "";
  let localityName = "";
  for (const component of addressComponents) {
    const { types, long_name: name } = component;
    if (types.includes("administrative_area_level_1")) {
      primaryAdminArea = name;
    }
    if (types.includes("locality")) {
      localityName = name;
    }
  }
  // Return the first available value between administrative area and locality
  return primaryAdminArea || localityName || "";
};

/**
 * Formats a Google address description by removing unwanted components such as "+", "untitled", "unknown", etc.
 * Returns the formatted address or a fallback alternative address with the country.
 */
export const formatGoogleAddressDescription = (
  address = "",
  fallbackAddress = "",
  country = "",
): string => {
  const addressParts = address ? address.split(" - ") : [];
  const filteredParts = addressParts.filter((part) => {
    const lowerCasePart = part.toLowerCase();
    return !(
      lowerCasePart.includes("+") ||
      lowerCasePart.includes("untitled") ||
      lowerCasePart.includes("unknown") ||
      lowerCasePart.includes("unnamed") ||
      lowerCasePart.includes(country.toLowerCase())
    );
  });
  if (!filteredParts.length) {
    return [fallbackAddress, country].filter(Boolean).join(", ");
  }
  return filteredParts.join(", ");
};

export const parseLocationCoordinates = (location?: {
  lat: string | number;
  lng: string | number;
}): { lat: number; lng: number } | undefined => {
  if (!location) {
    return undefined;
  }
  const { lat, lng } = location;
  return {
    lat: typeof lat === "string" ? parseFloat(lat) : lat,
    lng: typeof lng === "string" ? parseFloat(lng) : lng,
  };
};
